import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import DismissActionDialog from 'features/actions/components/DismissActionDialog'
import EditActionDialog from 'features/actions/components/EditActionDialog'
import RemedialTasksPanel from 'features/actions/components/RemedialTasksPanel'
import ImportedRemedialDetails from 'features/actions/components/ImportedRemedialDetails'
import RemedialFiles from 'features/actions/components/RemedialFiles'
import ActionActivityTimeline from 'features/actions/components/ActionActivityTimeline'
import StagePill from 'features/actions/workspace/StagePill'
import OwnerCell from 'features/actions/workspace/OwnerCell'
import DueCell from 'features/actions/workspace/DueCell'
import { canRejectAiFind, severityMeta, sourceLabel } from 'features/actions/workspace/remedialMeta'
import { formatDay } from 'features/actions/workspace/remedialDates'
import { OverdueExplainer } from 'features/actions/workspace/Explainers'
import { PrimaryButton } from 'features/actions/workspace/ui'
import { reopenActionRoutine, selectMutationLoading, updateActionRoutine } from 'features/actions/ducks'
import { remedialRefreshedRoutine } from 'features/actions/ducks/actions'
import ActionsAPI from 'services/ActionsAPI'

const NESTED_DIALOG_Z_INDEX = 1400

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

const focusablesIn = node => Array.from(node?.querySelectorAll(FOCUSABLE) || []).filter(el => !el.closest('[inert]'))

const inOtherDialog = (el, drawer) => {
  const dialog = el?.closest?.('[role="dialog"], [role="alertdialog"]')
  return !!dialog && dialog !== drawer
}

export const COMMENTS_SECTION_ID = 'remedial-drawer-comments'

const ActionDetailDrawer = ({ action, open, focus = null, onClose, onConvert, onComplete, onOpenSource }) => {
  const dispatch = useDispatch()
  const mutating = useSelector(selectMutationLoading)
  const [dismissOpen, setDismissOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const lastActionRef = useRef(null)
  const drawerRef = useRef(null)
  const headingRef = useRef(null)
  const returnFocusRef = useRef(null)
  const commentsRef = useRef(null)
  if (action) lastActionRef.current = action
  const shown = action || lastActionRef.current
  const shownId = shown?.id
  const refreshShown = useCallback(() => {
    if (!shownId) return
    ActionsAPI.get(shownId)
      .then(res => dispatch(remedialRefreshedRoutine.success(res.data)))
      .catch(() => {})
  }, [dispatch, shownId])

  const needsDetail = Boolean(open && shownId && shown?.metadata?.imported && shown?.files === undefined)
  useEffect(() => {
    if (needsDetail) refreshShown()
  }, [needsDetail, refreshShown])

  useEffect(() => {
    if (!open) return undefined
    const opener = document.activeElement
    returnFocusRef.current = opener && opener !== document.body ? opener : null
    const drawerNode = drawerRef.current
    headingRef.current?.focus({ preventScroll: true })
    return () => {
      const target = returnFocusRef.current
      returnFocusRef.current = null
      const active = document.activeElement
      const focusLost = !active || active === document.body || drawerNode?.contains(active)
      if (target && focusLost && document.contains(target)) target.focus({ preventScroll: true })
    }
  }, [open])

  useEffect(() => {
    if (!open || focus !== 'comments' || !shownId) return undefined
    const scrollToComments = () => {
      const drawer = drawerRef.current
      const section = commentsRef.current
      if (!drawer || !section) return
      drawer.scrollTop = Math.max(0, section.offsetTop - 16)
    }
    const frame = window.requestAnimationFrame(scrollToComments)
    const settle = window.setTimeout(scrollToComments, 400)
    return () => {
      window.cancelAnimationFrame(frame)
      window.clearTimeout(settle)
    }
  }, [open, focus, shownId])

  useEffect(() => {
    if (!open) return undefined
    const onKey = e => {
      const drawer = drawerRef.current
      if (dismissOpen || editOpen) return
      if (inOtherDialog(document.activeElement, drawer)) return
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !drawer) return
      const items = focusablesIn(drawer)
      if (!items.length) {
        e.preventDefault()
        headingRef.current?.focus()
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (!drawer.contains(active)) {
        e.preventDefault()
        first.focus()
      } else if (e.shiftKey && (active === first || active === headingRef.current)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, onClose, dismissOpen, editOpen])

  if (!shown) return null

  const sev = severityMeta(shown.severity)
  const perms = shown.permissions || {}
  const changeOwner = (target, ownerUserId) => dispatch(updateActionRoutine({ id: target.id, ownerUserId }))
  const changeDue = (target, dueDate) => dispatch(updateActionRoutine({ id: target.id, dueDate }))
  const showCreate = shown.stage === 'needs_decision' && perms.canConvert
  const showComplete = !!perms.canResolve && !!onComplete

  return createPortal(
    <>
      <Backdrop $open={open} onClick={onClose} />
      <Drawer ref={drawerRef} $open={open} role='dialog' aria-modal={open ? 'true' : undefined} aria-hidden={open ? undefined : 'true'} inert={!open} aria-label='Remedial details'>
        <DrawerHeader>
          <HeaderPills>
            <SeverityPip $colour={sev.color} $bg={sev.bg}>{sev.label}</SeverityPip>
            <StagePill stage={shown.stage} action={shown} withExplainer />
          </HeaderPills>
          <HeaderActions>
            <FullPageLink to={`/actions/${shown.id}`} aria-label='Open the full page for this remedial'>
              <OpenInNewIcon style={{ fontSize: 18 }} />
            </FullPageLink>
            {perms.canUpdate && (
              <IconBtn type='button' onClick={() => setEditOpen(true)} aria-label='Edit title, severity, description and due date'>
                <EditOutlinedIcon style={{ fontSize: 20 }} />
              </IconBtn>
            )}
            <IconBtn type='button' onClick={onClose} aria-label='Close remedial details'><CloseIcon /></IconBtn>
          </HeaderActions>
        </DrawerHeader>

        <DrawerTitle ref={headingRef} tabIndex={-1}>{shown.title}</DrawerTitle>
        {shown.description && <Description>{shown.description}</Description>}

        <MetaList>
          <MetaRow>
            <MetaLabel>Owner</MetaLabel>
            <MetaValue><OwnerCell action={shown} onChange={changeOwner} /></MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>Due</MetaLabel>
            <MetaValue><DueCell action={shown} onChange={changeDue} /><OverdueExplainer /></MetaValue>
          </MetaRow>
          {shown.suggestedDueBy && shown.suggestedDueBy !== shown.dueDate && (
            <MetaRow>
              <MetaLabel>Date suggested by the source</MetaLabel>
              <MetaValue>{formatDay(shown.suggestedDueBy)}</MetaValue>
            </MetaRow>
          )}
          {shown.buildingPassport?.name && (
            <MetaRow><MetaLabel>Building Passport</MetaLabel><MetaValue>{shown.buildingPassport.name}</MetaValue></MetaRow>
          )}
          <MetaRow>
            <MetaLabel>Source</MetaLabel>
            <MetaValue>
              {shown.sourceFile?.displayName && shown.buildingPassport?.id && !shown.sourceFile.deleted ? (
                <SourceButton type='button' onClick={() => onOpenSource?.(shown)}>
                  <DescriptionIcon style={{ fontSize: 16 }} />
                  <span>{shown.sourceFile.displayName}</span>
                  <OpenInNewIcon style={{ fontSize: 12, opacity: 0.7 }} />
                </SourceButton>
              ) : sourceLabel(shown.source)}
            </MetaValue>
          </MetaRow>
          {shown.sourcePageRef && (
            <MetaRow><MetaLabel>Page reference</MetaLabel><MetaValue>{shown.sourcePageRef}</MetaValue></MetaRow>
          )}
        </MetaList>

        {shown.files?.length > 0 && (
          <DrawerSection>
            <RemedialFiles files={shown.files} onOpen={file => onOpenSource?.({ ...shown, sourceFile: file })} />
          </DrawerSection>
        )}

        {shown.metadata?.imported && (
          <DrawerSection>
            <ImportedRemedialDetails action={shown} compact />
          </DrawerSection>
        )}

        <RemedialTasksPanel action={shown} onAddTask={target => onConvert?.(target)} onChanged={refreshShown} />

        {(showCreate || showComplete || canRejectAiFind(shown)) && (
          <ActionBar>
            {showCreate && (
              <PrimaryButton type='button' onClick={() => onConvert?.(shown)}>
                <CheckCircleOutlineIcon style={{ fontSize: 18 }} /> Create task
              </PrimaryButton>
            )}
            {showComplete && (
              <SecondaryBtn type='button' onClick={() => onComplete(shown)}>Mark as complete</SecondaryBtn>
            )}
            {canRejectAiFind(shown) && <SecondaryBtn type='button' onClick={() => setDismissOpen(true)}>Not a real issue</SecondaryBtn>}
          </ActionBar>
        )}

        {shown.stage === 'dismissed' && (
          <>
            <DismissedNotice>
              <strong>Dismissed</strong>
              {shown.dismissedReason && <DismissedReason>: {shown.dismissedReason}</DismissedReason>}
            </DismissedNotice>
            {perms.canReopen && (
              <ActionBar>
                <PrimaryButton
                  type='button'
                  onClick={() => { dispatch(reopenActionRoutine(shown.id)); onClose() }}
                  disabled={mutating}
                >
                  <RestartAltIcon style={{ fontSize: 18 }} /> Reopen remedial
                </PrimaryButton>
              </ActionBar>
            )}
          </>
        )}

        <DrawerSection id={COMMENTS_SECTION_ID} ref={commentsRef} data-testid='remedial-drawer-comments'>
          <ActionActivityTimeline actionId={shown.id} onCommented={refreshShown} />
        </DrawerSection>
      </Drawer>

      <DismissActionDialog
        action={shown}
        open={dismissOpen}
        onClose={() => setDismissOpen(false)}
        onDismissed={() => { setDismissOpen(false); onClose() }}
        zIndex={NESTED_DIALOG_Z_INDEX}
      />
      <EditActionDialog action={shown} open={editOpen} onClose={() => setEditOpen(false)} zIndex={NESTED_DIALOG_Z_INDEX} />
    </>,
    document.body
  )
}

export default ActionDetailDrawer

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`
const slideIn = keyframes`from { transform: translateX(100%); } to { transform: translateX(0); }`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  z-index: 1300;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
  animation: ${({ $open }) => ($open ? fadeIn : 'none')} 0.2s ease;
`

const Drawer = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  box-sizing: border-box;
  width: min(520px, 92vw);
  background: white;
  box-shadow: -8px 0 24px rgba(15, 23, 42, 0.12);
  z-index: 1301;
  padding: 22px 26px 32px;
  overflow-y: auto;
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  transform: translateX(${({ $open }) => ($open ? '0' : '100%')});
  transition: transform 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
  animation: ${({ $open }) => ($open ? slideIn : 'none')} 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`

const HeaderPills = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const SeverityPip = styled.div`
  background: ${({ $bg }) => $bg || '#e2e8f0'};
  color: ${({ $colour }) => $colour || '#475569'};
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`

const FullPageLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  min-height: 40px;
  padding: 4px;
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.haiti};
  text-decoration: none;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const IconBtn = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.haiti};
  padding: 4px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const DrawerTitle = styled.h2`
  font-size: 19px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 12px;
  line-height: 1.35;
  &:focus { outline: none; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.haiti}; outline-offset: 2px; }
`

const Description = styled.p`
  font-size: 14px;
  line-height: 1.55;
  color: #334155;
  margin: 0 0 18px;
  white-space: pre-wrap;
`

const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  padding-top: 14px;
`

const MetaRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const MetaLabel = styled.div`
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#64748b'};
`

const MetaValue = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.haiti};
  display: inline-flex;
  align-items: center;
`

const SourceButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  padding: 0;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  text-align: left;
  &:hover { text-decoration: underline; }
`

const ActionBar = styled.div`
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const SecondaryBtn = styled.button`
  padding: 10px 18px;
  background: white;
  color: ${({ theme }) => theme.colors.haiti};
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  min-height: 40px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const DismissedNotice = styled.div`
  margin-top: 18px;
  padding: 12px 14px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 13px;
  color: #475569;
`

const DismissedReason = styled.span`
  color: #64748b;
`

const DrawerSection = styled.div`
  margin: 16px 0 0;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`
